// 🔒 PROTECTED — PricePAL Compare
// Owner: Garry Yee | Do not modify without explicit written permission
// See /app/PROTECTED_CODE_REGISTRY.md for full rules
import React, { useState } from 'react';
import axios from 'axios';

const FeedbackWidget = ({ userId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [feedbackType, setFeedbackType] = useState('general');
  const [rating, setRating] = useState(0);
  const [timeSaved, setTimeSaved] = useState('');
  const [message, setMessage] = useState('');
  const [featureRequest, setFeatureRequest] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const feedback = {
        user_id: userId || 'anonymous',
        feedback_type: feedbackType,
        rating: rating > 0 ? rating : null,
        time_saved_minutes: timeSaved ? parseInt(timeSaved) : null,
        message,
        feature_request: featureRequest || null,
        page_url: window.location.href
      };

      await axios.post(`${backendUrl}/api/feedback/submit`, feedback);

      // Track the feedback submission
      await axios.post(`${backendUrl}/api/analytics/track`, {
        event_type: 'feedback_submitted',
        user_id: userId || 'anonymous',
        event_data: { feedback_type: feedbackType }
      });

      setSubmitted(true);
      setTimeout(() => {
        setIsOpen(false);
        setSubmitted(false);
        resetForm();
      }, 2000);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFeedbackType('general');
    setRating(0);
    setTimeSaved('');
    setMessage('');
    setFeatureRequest('');
  };

  return (
    <>
      {/* Floating Feedback Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 right-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-full shadow-lg hover:shadow-xl transition transform hover:scale-105 z-40 flex items-center gap-2 font-semibold text-sm"
        aria-label="Send Feedback"
      >
        💬 Feedback
      </button>

      {/* Feedback Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            {submitted ? (
              // Success Message
              <div className="p-8 text-center">
                <div className="text-6xl mb-4">🎉</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
                <p className="text-gray-600">Your feedback helps us improve PricePAL Compare for everyone.</p>
              </div>
            ) : (
              // Feedback Form
              <>
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-2xl">
                  <div className="flex items-center justify-between">
                    <h3 className="text-2xl font-bold">We'd Love Your Feedback!</h3>
                    <button
                      onClick={() => setIsOpen(false)}
                      className="text-white hover:text-gray-200 text-2xl"
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </div>
                  <p className="text-blue-100 mt-2">Help us make PricePAL Compare even better</p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                  {/* Feedback Type */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      What would you like to share?
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { value: 'time_saved', label: '⏱️ Time Saved', color: 'green' },
                        { value: 'feature_request', label: '💡 Feature Idea', color: 'blue' },
                        { value: 'bug_report', label: '🐛 Bug Report', color: 'red' },
                        { value: 'general', label: '💬 General', color: 'gray' }
                      ].map(type => (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => setFeedbackType(type.value)}
                          className={`px-4 py-3 rounded-lg text-sm font-medium transition ${
                            feedbackType === type.value
                              ? `bg-${type.color}-500 text-white`
                              : `bg-gray-100 text-gray-700 hover:bg-gray-200`
                          }`}
                        >
                          {type.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Rating (for all types) */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      How do you rate your experience?
                    </label>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map(star => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setRating(star)}
                          className={`text-3xl transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-yellow-400 rounded ${
                            star <= rating ? 'text-yellow-400' : 'text-gray-300 hover:text-yellow-200'
                          }`}
                          style={{ pointerEvents: 'auto', zIndex: 10 }}
                          aria-label={`Rate ${star} stars`}
                        >
                          ⭐
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Time Saved (only for time_saved type) */}
                  {feedbackType === 'time_saved' && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        How much time did PricePAL Compare save you? (minutes)
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={timeSaved}
                        onChange={(e) => setTimeSaved(e.target.value)}
                        placeholder="e.g., 30"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  )}

                  {/* Feature Request (only for feature_request type) */}
                  {feedbackType === 'feature_request' && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        What feature would you like to see?
                      </label>
                      <input
                        type="text"
                        value={featureRequest}
                        onChange={(e) => setFeatureRequest(e.target.value)}
                        placeholder="e.g., Recipe sharing with friends"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  )}

                  {/* Message */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {feedbackType === 'bug_report' ? 'Describe the issue' : 'Your feedback'}
                      <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      required
                      rows={4}
                      placeholder={
                        feedbackType === 'bug_report'
                          ? 'What went wrong? Please include steps to reproduce...'
                          : 'Tell us more...'
                      }
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    />
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={loading || !message}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 rounded-lg font-bold hover:from-blue-700 hover:to-purple-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? '📤 Sending...' : '📤 Send Feedback'}
                  </button>

                  <p className="text-xs text-gray-500 text-center">
                    We read every piece of feedback and use it to improve PricePAL Compare
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackWidget;
